import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import moment from 'moment'
import queryString from 'query-string'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  GetNotificationsQueryParams,
  GetNotificationsSuccessPayload,
} from 'src/app/Notifications/redux/notifications.actions'
import { TransactionExpenseCategorySplit } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import {
  NotificationListResponse,
  NotificationStatus,
} from 'src/models/notifications.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { TransactionSort } from 'src/models/transaction.models'
import {
  AdditionalField,
  usePutTransactionAdditionalFields,
} from 'src/queries/organizations/additionalFields.queries'
import {
  notificationsQueryKeys,
  TASKS_QK,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import {
  MutationMethod,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useRequestConfig,
} from 'src/queries/queries.utils'
import {
  TransactionsResponse,
  usePatchTransaction,
  usePatchTransactionExpenseData,
  useQueryTransactionDetail,
  useQueryTransactions,
  useQueryTransactionsCount,
  useSplitTransactionExpenseData,
  useUnsplitTransactionExpenseData,
} from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectActiveOrgIntegration,
  selectOrgIntegrationsListResponsesIsLoadedFromState,
} from 'src/redux/selectors/data/orgIntegrations.selectors'
import { AppliedExpenseDetailRequest } from 'src/requests/appliedExpenseCategory.request'
import { BackendErrorPayload, parseBackendErrors } from 'src/utils/error.utils'

export const useFetchNotifications = (
  queryParameters?: GetNotificationsQueryParams,
  options?: Partial<
    UseInfiniteQueryOptions<
      GetNotificationsSuccessPayload,
      AxiosError<BackendErrorPayload<string>>,
      InfiniteData<GetNotificationsSuccessPayload>,
      readonly unknown[],
      number
    >
  >,
) => {
  const requestConfig = useRequestConfig()
  return useInfiniteQuery({
    ...options,
    queryKey: notificationsQueryKeys.listWithFilters(queryParameters),
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...queryParameters,
        count: queryParameters?.count ?? 50,
        page: pageParam,
      }
      return await axios
        .get(
          `/users/notifications?${queryString.stringify(params)}`,
          requestConfig,
        )
        .then((response) => {
          const unboldedNotifcations = (
            response.data.notifications as NotificationListResponse[]
          ).map((notification) => {
            return {
              ...notification,
              // Replace all instances of '**' with '' in the body of the notification to address old formatting
              body: notification.body.replace(/\*\*/g, ''),
            }
          })
          return {
            pagination: response.data.pagination,
            notifications: unboldedNotifcations,
          }
        })
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.numberOfPages - 1) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    select: (
      response: InfiniteData<GetNotificationsSuccessPayload, number>,
    ): GetNotificationsSuccessPayload => ({
      notifications: response?.pages.flatMap((page) => page.notifications),
      pagination: {
        page: response?.pages[0]?.pagination.page,
        numberOfPages: response?.pages[0]?.pagination.numberOfPages,
        pageItemCount: response?.pages[0]?.pagination.pageItemCount,
        totalItems: response?.pages[0]?.pagination.totalItems,
      },
    }),
    initialPageParam: 0,
  })
}

export const useFetchNotificationDetails = (
  id?: string,
  options?: Partial<UseQueryOptions<NotificationListResponse>>,
) => {
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  return useQuery({
    ...options,
    enabled: !!id && options?.enabled,
    queryKey: notificationsQueryKeys.detail(id),
    queryFn: async (): Promise<NotificationListResponse> => {
      return await axios
        .get(`/users/notifications/${id}`, requestConfig)
        .then((response) => {
          // mark notification as read on the list response
          let originalReadStatus: NotificationStatus | undefined = undefined
          queryClient.setQueriesData(
            { queryKey: notificationsQueryKeys.list() },
            (
              oldData:
                | InfiniteData<GetNotificationsSuccessPayload, number>
                | undefined,
            ) => {
              if (!oldData) return oldData
              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  notifications: page.notifications.map((notification) => {
                    if (notification.id === id) {
                      originalReadStatus = notification.status
                      return {
                        ...notification,
                        status: NotificationStatus.read,
                      }
                    }
                    return notification
                  }),
                })),
              }
            },
          )
          if (originalReadStatus === NotificationStatus.unread) {
            // if the notification was previously unread,
            // decrement unread notifications count
            queryClient.setQueryData(
              notificationsQueryKeys.count(),
              (oldCount: number | undefined) => {
                if (oldCount === undefined) return oldCount
                return Math.max(0, oldCount - 1)
              },
            )
          }

          return response.data.notification as NotificationListResponse
        })
    },
  })
}

export const useFetchNotificationsCount = (
  options?: Partial<UseQueryOptions<number>>,
) => {
  const requestConfig = useRequestConfig()
  return useQuery({
    ...options,
    queryKey: notificationsQueryKeys.count(),
    queryFn: async (): Promise<number> => {
      return await axios
        .get(`/users/notifications/count`, requestConfig)
        .then((response) => {
          return response.data.count
        })
    },
  })
}

interface PostReadAllNotificationsVariables
  extends UseExtendMutationVariablesBody {}

export const usePostReadAllNotifications = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<string, PostReadAllNotificationsVariables>(
    MutationMethod.POST,
    {
      mutationUrl: () => '/users/notifications/mark',
      onSuccess: (variables) => {
        queryClient.invalidateQueries({
          queryKey: notificationsQueryKeys.listWithFilters(),
        })
        queryClient.invalidateQueries({
          queryKey: notificationsQueryKeys.count(),
        })
      },
    },
  )
}

export const useFetchTasks = () => {
  const { data: user } = useQueryOwnUser({})
  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const connectedPlatform = useSelector(
    selectActiveOrgIntegration,
  )?.connectedPlatform

  // Memoize the since date to prevent continuous refetching
  const sinceDate = useMemo(
    () => moment().subtract(90, 'day').format('YYYY-MM-DD'),
    [],
  )

  return useQueryTransactions({
    enabled: user?.id !== undefined,
    filters: {
      recipientOrPhysicalCardUserId: [user?.id || ''],
      tasks: true,
      since: sinceDate,
      sort: managerReviewRulesEnabled
        ? TransactionSort.tasksRejectedFirst
        : TransactionSort.dateClearDesc,
      connectedPlatform,
    },
    locationKey: TASKS_QK,
  })
}

export const useFetchTaskDetails = (id?: string) => {
  return useQueryTransactionDetail({ transactionId: id })
}

// Remote kill switch for the expensive always-mounted tasks-count query.
// Default off after the count-query invalidation amplification incident; flip
// enable_task_count on once the backend count path is safe to re-enable.
export const useIsTaskCountEnabled = (): boolean =>
  useSelector(selectFeatureFlaggerFromState).enable_task_count

export const useFetchTasksCount = () => {
  const { data: user } = useQueryOwnUser({})
  const recipientOrPhysicalCardUserId = user?.id
  const isTaskCountEnabled = useIsTaskCountEnabled()
  const orgIntegrationsLoaded = useSelector(
    selectOrgIntegrationsListResponsesIsLoadedFromState,
  )

  const connectedPlatform = useSelector(
    selectActiveOrgIntegration,
  )?.connectedPlatform

  // Memoize the since date to prevent continuous refetching
  const sinceDate = useMemo(
    () => moment().subtract(90, 'day').format('YYYY-MM-DD'),
    [],
  )

  return useQueryTransactionsCount({
    filters: {
      recipientOrPhysicalCardUserId: recipientOrPhysicalCardUserId
        ? [recipientOrPhysicalCardUserId]
        : undefined,
      tasks: true,
      since: sinceDate,
      connectedPlatform,
    },
    enabled:
      isTaskCountEnabled &&
      !!recipientOrPhysicalCardUserId &&
      orgIntegrationsLoaded,
    locationKey: TASKS_QK,
  })
}

// TODO: check that useFetchTasksCount updates when a task is completed
// this can only be checked once the task details section is implemented

export const usePatchTask = () => {
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  // for filters used to invalidate task queries
  const { data: user } = useQueryOwnUser({})
  const connectedPlatform = useSelector(
    selectActiveOrgIntegration,
  )?.connectedPlatform
  // Memoize the since date to prevent continuous refetching
  const sinceDate = useMemo(
    () => moment().subtract(90, 'day').format('YYYY-MM-DD'),
    [],
  )

  const listKey = transactionsQueryKeys.list(
    {
      recipientOrPhysicalCardUserId: [user?.id || ''],
      tasks: true,
      since: sinceDate,
      sort: managerReviewRulesEnabled
        ? TransactionSort.tasksRejectedFirst
        : TransactionSort.dateClearDesc,
      connectedPlatform,
    },
    /* locationKey */ TASKS_QK,
  )

  const patchReceipts = usePatchTransaction()
  const patchExpDetails = usePatchTransactionExpenseData()
  const patchAdditionalFields = usePutTransactionAdditionalFields()
  const splitTransactionHook = useSplitTransactionExpenseData()
  const unsplitTransactionHook = useUnsplitTransactionExpenseData()

  const mutate = async (data: {
    transactionId: string
    attachments?: ReceiptAttachmentModel[]
    expenseDetails?: AppliedExpenseDetailRequest[]
    splits?: TransactionExpenseCategorySplit[]
    notes?: string
    additionalFields?: AdditionalField[]
  }) => {
    // First, get the current transaction details to check if it's already split
    const currentTransaction = await queryClient.fetchQuery({
      queryKey: transactionsQueryKeys.detail(data.transactionId),
      queryFn: async () =>
        (await axios.get(`/transactions/${data.transactionId}`, requestConfig))
          .data,
    })

    // update attachments and notes in sequence to avoid race conditions
    let patchReceiptsResponse
    if ((data.attachments && data.attachments.length > 0) || data.notes)
      patchReceiptsResponse = await patchReceipts.mutateAsync({
        transactionId: data.transactionId,
        body: {
          receiptAttachmentIds:
            data.attachments && data.attachments.length > 0
              ? data.attachments.map((attachment) => attachment.id)
              : undefined,
          notes: data.notes || undefined,
        },
      })

    let patchAdditionalFieldsResponse
    if (data.additionalFields && data.additionalFields.length > 0)
      patchAdditionalFieldsResponse = await patchAdditionalFields.mutateAsync({
        transactionId: data.transactionId,
        body: {
          additionalFields: data.additionalFields,
        },
      })

    let patchExpDetailsResponse
    // Handle splits logic similar to TransactionDetail's handleSaveSplitExpenseCategories
    if (data.splits !== undefined) {
      if (data.splits.length === 0) {
        // No splits passed in - handle unsplitting or regular expense details
        if (currentTransaction.isSplit) {
          // Transaction is already split, unsplit it
          patchExpDetailsResponse = await unsplitTransactionHook.mutateAsync({
            transactionId: data.transactionId,
            body: {
              expenseDetails: data.expenseDetails || [],
            },
          })
        } else if (data.expenseDetails) {
          // Transaction is not split, save regular expense details
          patchExpDetailsResponse = await patchExpDetails.mutateAsync({
            transactionId: data.transactionId,
            body: {
              expenseDetails: data.expenseDetails,
            },
          })
        }
      } else {
        // Splits passed in - save them (works for both already split and not split transactions)
        const buildSplitExpenseDetailRequests = (
          splits: TransactionExpenseCategorySplit[],
        ) => {
          return splits.flatMap((split, splitIndex) => {
            const splitValue =
              typeof split.splitValue === 'number'
                ? split.splitValue
                : Number.parseFloat(String(split.splitValue ?? 0))
            const splitAmountCents = Math.round(
              (Number.isFinite(splitValue) ? splitValue : 0) * 100,
            )

            return Object.values(split.expenseCategories ?? {}).flatMap(
              (mapping) => {
                const labelId = mapping.labelId ?? mapping.labelName
                if (!labelId || String(labelId).trim() === '') return []
                return [
                  {
                    categoryId: mapping.categoryId,
                    labelId: String(labelId),
                    splitIndex,
                    splitAmountCents,
                  },
                ]
              },
            )
          })
        }

        patchExpDetailsResponse = await splitTransactionHook.mutateAsync({
          transactionId: data.transactionId,
          body: {
            expenseDetails: buildSplitExpenseDetailRequests(data.splits),
          },
        })
      }
    } else if (data.expenseDetails) {
      // No splits provided, but expenseDetails provided - handle regular expense details
      if (currentTransaction.isSplit) {
        // Transaction is currently split but no splits passed in - unsplit it and save expense details
        patchExpDetailsResponse = await unsplitTransactionHook.mutateAsync({
          transactionId: data.transactionId,
          body: {
            expenseDetails: data.expenseDetails,
          },
        })
      } else {
        // Transaction is not split - save regular expense details
        patchExpDetailsResponse = await patchExpDetails.mutateAsync({
          transactionId: data.transactionId,
          body: {
            expenseDetails: data.expenseDetails,
          },
        })
      }
    }

    // if at least one of the updates was successful
    // (if some were successful and some failed, we still want to invalidate the queries;
    // the unfinished task will still be shown in the task list)
    return (
      patchExpDetailsResponse ||
      patchReceiptsResponse ||
      patchAdditionalFieldsResponse
    )
  }

  const patchReceiptsError = patchReceipts.error
    ? parseBackendErrors(patchReceipts.error).topLevelError
    : undefined
  const patchAdditionalFieldsError = patchAdditionalFields.error
    ? parseBackendErrors(patchAdditionalFields.error).topLevelError
    : undefined
  const patchExpDetailsError = patchExpDetails.error
    ? parseBackendErrors(patchExpDetails.error).topLevelError
    : undefined
  const splitError = splitTransactionHook.error
    ? parseBackendErrors(splitTransactionHook.error).topLevelError
    : undefined
  const unsplitError = unsplitTransactionHook.error
    ? parseBackendErrors(unsplitTransactionHook.error).topLevelError
    : undefined

  const deleteTaskAndUpdateCounts = (taskId: string) => {
    queryClient.setQueryData<
      InfiniteData<TransactionsResponse, number> | undefined
    >(listKey, (old) => {
      if (!old) return undefined
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          report: {
            ...page.report,
            transactions: page.report.transactions.filter(
              (txn) => txn.id !== taskId,
            ),
          },
        })),
      }
    })

    queryClient.setQueryData(
      transactionsQueryKeys.count(
        {
          recipientOrPhysicalCardUserId: [user?.id || ''],
          tasks: true,
          since: sinceDate,
          connectedPlatform,
        },
        /* locationKey */ TASKS_QK,
      ),
      (oldCount: { count: number } | undefined) => {
        if (oldCount?.count === undefined) return oldCount
        return { count: Math.max(0, oldCount.count - 1) }
      },
    )
  }

  return {
    mutate,
    deleteTaskAndUpdateCounts: deleteTaskAndUpdateCounts,
    isPending:
      patchReceipts.isPending ||
      patchAdditionalFields.isPending ||
      patchExpDetails.isPending ||
      splitTransactionHook.isPending ||
      unsplitTransactionHook.isPending,
    errors: [
      ...(patchReceiptsError ? [patchReceiptsError] : []),
      ...(patchAdditionalFieldsError ? [patchAdditionalFieldsError] : []),
      ...(patchExpDetailsError ? [patchExpDetailsError] : []),
      ...(splitError ? [splitError] : []),
      ...(unsplitError ? [unsplitError] : []),
    ],
  }
}
